// react
import { MouseEvent, useEffect, useState } from 'react';

// types
import {
	Account,
	Contract,
	Entity,
	ExpenseType,
	Loan,
	Payment,
	Transaction,
	WithdrawalApplication,
} from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Grid, Typography } from '@mui/material';

// dialogs
import { DialogActions, DialogActionsProps } from '../DialogActions/DialogActions';

// ui
import { ChipStatus } from '../../ui/ChipStatus/ChipStatus';
import { Loading } from '../../ui/Loading/Loading';
import { TableInfo, TableInfoProps } from '../../ui/TableInfo/TableInfo';

// links
import { LinkAccount } from '../../links/LinkAccount/LinkAccount';
import { LinkEntity } from '../../links/LinkEntity/LinkEntity';

// utils
import { formatCurrency } from '../../../utils/currency';
import { translateTransactionType } from '../../../utils/translations';
import { Table, TableHeader } from '../../ui/Table/Table';
import { BlockchainHash } from '../../ui/BlockchainHash/BlockchainHash';
import { formatDate } from '../../../utils/dates';

// props
export interface DialogTransactionProps {
	isOpen: boolean;
	onClose: (e?: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
	onSuccess?: () => void;
}

export const DialogTransaction = ({ isOpen, onClose }: DialogTransactionProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const [searchParams] = useSearchParams();
	const { t } = useTranslation();

	// params
	const paramTransaction = searchParams.get('dialogTransaction');

	// state
	const [transaction, setTransaction] = useState<Transaction | null>(null);

	// fetch transaction
	const { isLoading: isLoadingTransaction } = useFetch({
		isEnabled: Boolean(paramTransaction),
		url: `${process.env.REACT_APP_API_URL}/transactions/${paramTransaction}?populate=contract,expenseType,fromAccount,fromEntity,payments.conversions,payments.loan,toAccount,toEntity,withdrawalApplication`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setTransaction(res.data[0]);
			}
		},
	});

	const handleClose = (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
		if (onClose) {
			onClose(e);
		}
	};

	const renderContent = () => {
		if (isLoadingTransaction) {
			return (
				<Grid item xs={12}>
					<Grid alignItems="center" container justifyContent="center">
						<Grid item>
							<Loading />
						</Grid>
					</Grid>
				</Grid>
			);
		}

		if (transaction) {
			const contract = transaction.contract as Contract;
			const expenseType = transaction.expenseType as ExpenseType;
			const fromAccount = transaction.fromAccount as Account;
			const fromEntity = transaction.fromEntity as Entity;
			const toAccount = transaction.toAccount as Account;
			const toEntity = transaction.toEntity as Entity;

			const withdrawalApplication = transaction.withdrawalApplication as WithdrawalApplication;

			const data: TableInfoProps['data'] = [];

			if (transaction.id) {
				data.push({
					label: t('id'),
					value: String(transaction.id),
				});
			}

			if (transaction.transactionRefunded) {
				data.push({
					label: t('idTransactionRefunded'),
					value: String(transaction.transactionRefunded),
				});
			}

			if (transaction.amount) {
				data.push({
					label: t('amount'),
					value: formatCurrency({
						currency: transaction.currency,
						value: transaction.amount,
					}),
				});
			}

			if (transaction.tax) {
				data.push({
					label: t('tax'),
					value: formatCurrency({
						currency: transaction.currency,
						value: transaction.tax,
					}),
				});
			}

			if (transaction.amountReceived && transaction.currencyReceived) {
				data.push({
					label: t('received'),
					value: formatCurrency({
						currency: transaction.currencyReceived,
						value: transaction.amountReceived,
					}),
				});
			}

			if (transaction.total) {
				data.push({
					label: t('total'),
					value: formatCurrency({
						currency: transaction.currency,
						value: transaction.total,
					}),
				});
			}

			if (transaction.totalRefunded) {
				data.push({
					label: t('totalRefunded'),
					value: formatCurrency({
						currency: transaction.currency,
						value: transaction.totalRefunded,
					}),
				});
			}

			if (transaction.date) {
				data.push({
					label: t('date'),
					value: formatDate({ date: transaction.date }),
				});
			}

			if (expenseType) {
				data.push({
					label: t('expenseType'),
					value: expenseType.name,
				});
			}

			if (contract) {
				data.push({
					label: t('contract'),
					value: contract.num,
				});
			}

			if (transaction.status) {
				data.push({
					label: t('status'),
					value: <ChipStatus status={transaction.status} />,
				});
			}

			if (fromEntity) {
				data.push({
					label: t('from'),
					value: <LinkEntity entity={fromEntity} />,
				});
			}

			if (fromAccount) {
				data.push({
					label: t('fromAccount'),
					value: <LinkAccount account={fromAccount} />,
				});
			}

			if (toEntity) {
				data.push({
					label: t('to'),
					value: <LinkEntity entity={toEntity} />,
				});
			}

			if (toAccount) {
				data.push({
					label: t('toAccount'),
					value: <LinkAccount account={toAccount} />,
				});
			}

			if (withdrawalApplication) {
				data.push({
					label: t('withdrawalApplication'),
					value: withdrawalApplication.idExternal,
				});
			}

			return (
				<>
					<Grid item xs={12}>
						<TableInfo data={data} />
					</Grid>
					{transaction.payments.length > 0 && (
						<Grid item xs={12}>
							<Table
								borderRadius={2}
								color="grey"
								data={transaction.payments}
								headers={[
									{
										label: t('source'),
										render: (loan: Loan) => {
											return <Typography fontWeight={600}>{loan.code || t('na')}</Typography>;
										},
										value: 'loan',
									},
									{
										label: t('amount'),
										render: (amount: number, header: TableHeader, payment: Payment) => {
											return formatCurrency({
												currency: payment.currency,
												value: amount + (payment.tax || 0),
											});
										},
										value: 'amount',
									},
									{
										label: t('hash'),
										render: (
											blkTransaction: Payment['blkTransaction'],
											header: TableHeader,
											payment: Payment
										) => {
											return (
												<BlockchainHash
													hash={blkTransaction?.id}
													hasIntegrity={payment.hasIntegrity}
												/>
											);
										},
										value: 'blkTransaction',
									},
								]}
								hasBorder={true}
								isCondensed={true}
							/>
						</Grid>
					)}
				</>
			);
		}

		return (
			<Grid item xs={12} sx={{ textAlign: 'center' }}>
				{t('empty.transaction.content')}
			</Grid>
		);
	};

	const propsDialogActions: Partial<DialogActionsProps> = {};

	if (!isLoadingTransaction) {
		if (transaction) {
			propsDialogActions.IconCircleTransactionProps = {
				hasTooltip: false,
				transaction,
			};
		} else {
			propsDialogActions.IconCircleProps = {
				type: 'default',
			};
		}
	}

	// reset transaction when isOpen is false
	useEffect(() => {
		return () => {
			if (!isOpen) {
				setTransaction(null);
			}
		};
	}, [isOpen]);

	return (
		<DialogActions
			actions={[
				{
					children: t('done'),
					onClick: handleClose,
					variant: 'contained',
				},
			]}
			alignItems="center"
			hasClose={true}
			isOpen={isOpen}
			maxWidth={480}
			onClose={handleClose}
			subtitle={{
				children: isLoadingTransaction ? undefined : t('transaction'),
			}}
			title={{
				children: transaction ? translateTransactionType(transaction.type, t) : undefined,
			}}
			{...propsDialogActions}>
			<Grid container spacing={2}>
				{renderContent()}
			</Grid>
		</DialogActions>
	);
};
